import {
  createContext,
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";

export enum Theme {
  Light = "light",
  Dark = "dark",
}

export enum Zoom {
  Compact = "Compact",
  Comfortable = "Comfortable",
  Default = "Default",
}

const ZoomFactor = {
  [Zoom.Compact]: 0.8,
  [Zoom.Comfortable]: 0.85,
  [Zoom.Default]: 0.9,
};

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  zoom: Zoom;
  setZoom: (zoom: Zoom) => void;
  desktopMode: boolean;
  setDesktopMode: (enabled: boolean) => void;
  ref: RefObject<HTMLButtonElement | null>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem("theme") as Theme) || Theme.Light,
  );
  const [zoom, setZoom] = useState<Zoom>(
    () => (localStorage.getItem("zoom") as Zoom) || Zoom.Default,
  );
  const [desktopMode, setDesktopMode] = useState<boolean>(
    () => localStorage.getItem("desktopMode") === "true",
  );
  const ref = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove(theme === "dark" ? "light" : "dark");
    root.classList.add(theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    const x = window.innerWidth / 2;
    const y = window.innerHeight / 2;
    const endRadius = Math.hypot(window.innerWidth, window.innerHeight);

    const transition = document.startViewTransition(() => {
      flushSync(() => {
        setTheme((prev) => (prev === Theme.Light ? Theme.Dark : Theme.Light));
      });
    });

    transition.ready.then(() => {
      document.documentElement.animate(
        {
          clipPath: [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${endRadius}px at ${x}px ${y}px)`,
          ],
        },
        {
          duration: 1500, // match gif length
          easing: "ease-in-out",
          pseudoElement: "::view-transition-new(root)",
        },
      );
    });
  };

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--zoom-level",
      ZoomFactor[zoom].toString(),
    );
    localStorage.setItem("zoom", zoom);
  }, [zoom]);

  useEffect(() => {
    localStorage.setItem("desktopMode", String(desktopMode));
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      if (desktopMode) {
        viewport.setAttribute(
          "content",
          "width=1440, initial-scale=0.5, user-scalable=yes",
        );
      } else {
        viewport.setAttribute(
          "content",
          "width=device-width, initial-scale=1.0",
        );
      }
    }
    if (desktopMode) {
      document.documentElement.classList.add("desktop-mode");
    } else {
      document.documentElement.classList.remove("desktop-mode");
    }
  }, [desktopMode]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme,
        zoom,
        setZoom,
        desktopMode,
        setDesktopMode,
        ref,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within a ThemeProvider");
  return context;
};
